import React, { useState, useEffect } from 'react';
import { getCoachingTopics, pinCoachingTopic } from '../../Services/coaching';
import { resolveTopicAction } from '../../Utils/topicAction';
import './styles.css';

/**
 * CoachingTopicsModal - Displays coaching topics grouped by category
 *
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Callback when modal is closed
 * @param {function} onSelectTopic - Callback when a coaching-typed topic is selected (receives topic object)
 * @param {function} onNavigationAction - Callback when a navigation-typed topic is tapped; receives (topic, target).
 * @param {function} onUnsupportedAction - Optional callback when a topic's action cannot be honored (e.g. missing platform target).
 * @param {string} platform - 'web' or 'mobile' (default: 'web')
 * @param {string} sessionType - 'voice' or 'keyboard'
 * @param {boolean} slideFromLeft - Whether to use slide-from-left animation (for back navigation)
 */
const CoachingTopicsModal = ({
  isOpen,
  onClose,
  onSelectTopic,
  onNavigationAction,
  onUnsupportedAction,
  platform = 'web',
  sessionType = 'voice',
  slideFromLeft = false,
}) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTopics = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getCoachingTopics(platform, sessionType);
      // Sort categories by order
      const sortedCategories = (data.categories || []).sort((a, b) => a.order - b.order);
      setCategories(sortedCategories);
    } catch (err) {
      console.error('Error fetching coaching topics:', err);
      setError('Failed to load coaching topics. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch topics when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchTopics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, platform, sessionType]);

  // handlePinToggle is available for future use when pin functionality is enabled
  const handlePinToggle = async (e, topic) => {
    e.stopPropagation();
    try {
      await pinCoachingTopic(topic.topic_id, !topic.is_pinned);
      // Update local state optimistically
      setCategories(prevCategories =>
        prevCategories.map(category => ({
          ...category,
          topics: category.topics.map(t =>
            t.topic_id === topic.topic_id
              ? { ...t, is_pinned: !t.is_pinned }
              : t
          )
        }))
      );
    } catch (err) {
      console.error('Error toggling pin:', err);
      // Optionally show a toast notification here
    }
  };

  const handleTopicClick = (topic, category) => {
    const enrichedTopic = {
      ...topic,
      categoryId: category.category_id,
      categoryName: category.name,
    };

    const resolved = resolveTopicAction(topic);

    if (resolved.kind === 'navigation') {
      if (onNavigationAction) {
        onNavigationAction(enrichedTopic, resolved.target);
      } else if (onSelectTopic) {
        onSelectTopic(enrichedTopic);
      }
      return;
    }

    if (resolved.kind === 'unsupported') {
      if (onUnsupportedAction) {
        onUnsupportedAction(enrichedTopic, resolved.reason);
      } else {
        console.warn('[CoachingTopics] unsupported action for topic', topic?.topic_id, resolved.reason);
      }
      return;
    }

    if (onSelectTopic) {
      onSelectTopic(enrichedTopic);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="coaching-topics-overlay" onClick={onClose}>
      <div
        className={`coaching-topics-modal ${slideFromLeft ? 'coaching-topics-slide-in' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="coaching-topics-header">
          <button className="coaching-topics-close" onClick={onClose}>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M15 5L5 15M5 5L15 15" stroke="#7E7F83" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <h2 className="coaching-topics-title">Session Topics</h2>
        </div>

        <div className="coaching-topics-content">
          {loading ? (
            <div className="coaching-topics-loading">
              <div className="coaching-topics-spinner" />
              <p>Loading topics...</p>
            </div>
          ) : error ? (
            <div className="coaching-topics-error">
              <p>{error}</p>
              <button onClick={fetchTopics} className="coaching-topics-retry">
                Try Again
              </button>
            </div>
          ) : categories.length === 0 ? (
            <div className="coaching-topics-empty">
              <p>No session topics available.</p>
            </div>
          ) : (
            <div className="coaching-topics-list">
              {categories.map((category) => (
                <div key={category.category_id} className="coaching-topics-category">
                  <h3 className="coaching-topics-category-name">{category.name}</h3>
                  <div className="coaching-topics-items">
                    {category.topics
                      .filter(topic => topic.is_active)
                      .sort((a, b) => {
                        // Pinned topics first, then by order
                        if (a.is_pinned !== b.is_pinned) {
                          return a.is_pinned ? -1 : 1;
                        }
                        return a.order - b.order;
                      })
                      .map((topic) => (
                        <div
                          key={topic.topic_id}
                          className={`coaching-topics-item ${topic.is_pinned ? 'pinned' : ''}`}
                          onClick={() => handleTopicClick(topic, category)}
                        >
                          <span className="coaching-topics-item-title">{topic.title}</span>
                          {/* Pin button - hidden for now as per design */}
                          {/* <button
                            className="coaching-topics-pin-btn"
                            onClick={(e) => handlePinToggle(e, topic)}
                            title={topic.is_pinned ? 'Unpin topic' : 'Pin topic'}
                          >
                            {topic.is_pinned ? <PinFilledIcon /> : <PinIcon />}
                          </button> */}
                        </div>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CoachingTopicsModal;
